import { VoiceIntent, ParsedIntent } from "./intent-parser";

export interface VoiceCommand {
  intent: VoiceIntent;
  description: string;
  execute: (parsed: ParsedIntent) => void | Promise<void>;
  validate?: (parsed: ParsedIntent) => boolean;
}

export interface CommandRegistrySettings {
  onCommandNotFound?: (parsed: ParsedIntent) => void;
  onCommandError?: (error: Error, parsed: ParsedIntent) => void;
}

export class CommandRegistry {
  private commands: Map<VoiceIntent, VoiceCommand> = new Map();
  private settings: CommandRegistrySettings;

  constructor(settings: CommandRegistrySettings = {}) {
    this.settings = settings;
  }

  public register(command: VoiceCommand): void {
    this.commands.set(command.intent, command);
  }

  public unregister(intent: VoiceIntent): void {
    this.commands.delete(intent);
  }

  public async execute(parsed: ParsedIntent): Promise<void> {
    if (parsed.intent === VoiceIntent.UNKNOWN) {
      this.settings.onCommandNotFound?.(parsed);
      return;
    }

    const command = this.commands.get(parsed.intent);

    if (!command) {
      this.settings.onCommandNotFound?.(parsed);
      return;
    }

    if (command.validate && !command.validate(parsed)) {
      this.settings.onCommandNotFound?.(parsed);
      return;
    }

    try {
      await command.execute(parsed);
    } catch (error) {
      this.settings.onCommandError?.(error as Error, parsed);
    }
  }

  public getRegisteredCommands(): VoiceCommand[] {
    return Array.from(this.commands.values());
  }

  public hasCommand(intent: VoiceIntent): boolean {
    return this.commands.has(intent);
  }
}
